import { editorLang, editorPath } from '@/components/editorPages/routes';
import type { ConsoleToolId } from '@/components/landing/ToolCompass';

// THE STUDIOS (Studio Blueprint, docs/studio-blueprint.md, section 02): one
// list that every door, menu, tab bar, command menu and footer reads, so a
// studio is named, priced and linked the same way everywhere. Each studio has
// its own URL and first screen; home is only the directory.
//
// `entry` is the PRICE where the studio charges (owner 2026-10-16: no "free"
// on a paid studio; the storyboard you approve before paying is not a free
// plan). "Free" only where nothing is ever charged to see the result: the
// storybook preview, the editor.
//
// `entry` is what the first step costs, shown on every door ("Free
// storyboard", "from 24 cr"). Amber means money, teal means free: `free`
// picks the colour. `audience` keeps family and creator media apart: corrido
// pictures never appear next to a storybook.

export type StudioId = 'music' | 'storybook' | 'ads' | 'story' | 'editor' | 'quick';

export interface Studio {
  id: StudioId;
  /** i18n stem under `studios.<id>`: name, door, entry, promise. */
  key: string;
  /** The page; the editor's depends on the language. */
  path: (lang: string) => string;
  /** A real output of this studio (poster image), never a stock picture. */
  poster: string;
  /** The same output as a clip, where one exists. */
  clip?: string;
  /** The first step is free (teal) or costs credits / money (amber). */
  free: boolean;
  audience: 'creator' | 'family' | 'everyone';
  /** The Director console stop this studio page runs, if any. */
  consoleTool?: ConsoleToolId;
}

export const STUDIOS: readonly Studio[] = [
  {
    id: 'music', key: 'music', path: () => '/music',
    poster: '/promo/poster_belico.jpg', clip: '/promo/corrido_belico.mp4',
    free: false, audience: 'creator', consoleTool: 'corrido',
  },
  {
    id: 'storybook', key: 'storybook', path: () => '/storybook',
    poster: '/storybook/promo/style-animated3d-640.webp',
    free: true, audience: 'family',
  },
  {
    id: 'ads', key: 'ads', path: () => '/ads',
    poster: '/promo/poster_ad_energy.jpg', clip: '/promo/ad_energy_spot.mp4',
    free: false, audience: 'creator', consoleTool: 'ads',
  },
  {
    id: 'story', key: 'story', path: () => '/tiktok',
    poster: '/promo/poster_story_1.jpg', clip: '/promo/story_clip_1.mp4',
    free: false, audience: 'creator', consoleTool: 'story',
  },
  {
    id: 'editor', key: 'editor', path: (lang) => editorPath('hub', editorLang(lang)),
    poster: '/promo/chopbox-editor-800.webp',
    free: true, audience: 'everyone',
  },
  {
    id: 'quick', key: 'quick', path: () => '/tools',
    poster: '/quicktools/captions-poster.jpg',
    free: false, audience: 'everyone',
  },
] as const;

/** The four paid studios behind the home page's doors, in the blueprint's order. */
export const DOOR_STUDIOS: readonly StudioId[] = ['music', 'storybook', 'ads', 'story'];

export const studioById = (id: StudioId): Studio => STUDIOS.find((s) => s.id === id) as Studio;

/** The studio page that runs a Director console stop. */
export function studioPathForTool(tool: string): string | null {
  const s = STUDIOS.find((x) => x.consoleTool === tool);
  return s ? s.path('en') : null;
}
